import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import {
  DEFAULT_SMART_TRACKING_CONFIG,
  DEFAULT_SMART_TRACKING_RULES,
  SMART_TRACKING_RULE_IDS,
} from "./types";
import type { SmartTrackingConfig, SmartTrackingRules } from "./types";

interface SmartTrackingBody {
  data?: { data?: unknown } | unknown;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

export const normalizeRules = (value: unknown): SmartTrackingRules => {
  const raw = asRecord(value);
  return SMART_TRACKING_RULE_IDS.reduce(
    (acc, id) => ({
      ...acc,
      [id]:
        typeof raw[id] === "boolean"
          ? (raw[id] as boolean)
          : DEFAULT_SMART_TRACKING_RULES[id],
    }),
    {} as SmartTrackingRules,
  );
};

export const normalizeSmartTrackingConfig = (
  value: unknown,
): SmartTrackingConfig => {
  const raw = asRecord(value);
  return {
    company_id: typeof raw.company_id === "string" ? raw.company_id : null,
    enabled:
      typeof raw.enabled === "boolean"
        ? raw.enabled
        : DEFAULT_SMART_TRACKING_CONFIG.enabled,
    rules: normalizeRules(raw.rules),
    updated_by: typeof raw.updated_by === "string" ? raw.updated_by : null,
  };
};

export function useSmartTrackingConfig() {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.smartTracking.config(cid),
    queryFn: ({ signal }) =>
      getJson<SmartTrackingBody>("/smartTracking", undefined, { signal }).then(
        (body) =>
          normalizeSmartTrackingConfig(asRecord(body.data).data ?? body.data),
      ),
  });
}
